import React from "react";
import { API } from "../API";
import { isLoading, Loading } from "../Loading";
import { PatientDocumentTypeId, VisitInstanceId } from "../messages/ObjectIds";
import {
  CaregiverPatientDocument,
  CaregiverPatientDocumentResponse,
  PatientDocumentScanCreate,
} from "../messages/PatientDocuments";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import useModal from "./useModal";

interface Props {
  visitInstanceId: VisitInstanceId;
  caregiver: AppCaregiver | Loading;
  api: API;
}

type State =
  | { type: "Loading" }
  | { type: "Error" }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      freeScans: CaregiverPatientDocument.FreeScan[];
      documents: (
        | CaregiverPatientDocument.Unanswered
        | CaregiverPatientDocument.Answered
        | CaregiverPatientDocument.Scanned
        | CaregiverPatientDocument.ManualSubmission
      )[];
    };

const initialState = { type: "Loading" as const };

const useVisitInstanceDocuments = (props: Props) => {
  const abortToken = React.useRef(false);

  const modal = useModal();

  const [state, setState] = React.useState<State>(initialState);
  const [isDeletingDocument, setDeletingDocument] = React.useState(false);
  const [isUploadingDocument, setUploadingDocument] = React.useState(false);

  const promptDeleteFreeScan = (document: CaregiverPatientDocument.FreeScan) => {
    modal.setModalAsPrompt({
      title: translate(STRINGS.ACCEPT_THE_VISIT_AT),
      message: `${translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_DELETE)} ${document.title}?}`,
      closeText: translate(STRINGS.I_HAVE_CHANGED_MY_MIND),
      actions: [
        {
          key: "close",
          children: translate(STRINGS.YES_DELETE),
          onClick: () => deleteFreeScan(document),
          variant: "soft",
          color: "black",
        },
      ],
    });
  };

  const setDocumentsState = React.useCallback(
    ({ documents, freeScans }: CaregiverPatientDocumentResponse) => {
      if (isLoading(props.caregiver)) {
        return;
      }

      if (abortToken.current) {
        return;
      }

      const dutySheetDocument = PatientDocumentTypeId.wrap(-12345);

      setState({
        type: "Ready",
        documents: documents.filter((document) => document.documentTypeId !== dutySheetDocument),
        freeScans: freeScans,
        caregiver: props.caregiver,
      });
    },
    [props.caregiver]
  );

  const deleteFreeScan = async (freeScan: CaregiverPatientDocument.FreeScan) => {
    if (state.type !== "Ready") {
      console.error("Can't delete document while state is not ready");
      return;
    }

    try {
      setDeletingDocument(true);

      await props.api.deleteScannedScheduledVisitDocument(
        state.caregiver.id,
        props.visitInstanceId,
        freeScan.id
      );

      setState((prevState) => {
        if (prevState.type !== "Ready") {
          return prevState;
        }

        return {
          ...prevState,
          freeScans: prevState.freeScans.filter((x) => x.id !== freeScan.id),
        };
      });
    } catch (e) {
      console.error(e);
      setState({ type: "Error" });
    } finally {
      setDeletingDocument(false);
    }
  };

  const uploadNewDocument = async (data: PatientDocumentScanCreate) => {
    setUploadingDocument(true);

    try {
      setDocumentsState(
        await props.api.uploadScannedScheduledVisitDocument(data, props.visitInstanceId)
      );
    } catch (e) {
      console.error(e);
      setState({ type: "Error" });
    } finally {
      setUploadingDocument(false);
    }
  };

  const initDocumentsState = React.useCallback(async () => {
    setState({ type: "Loading" });

    try {
      setDocumentsState(await props.api.getVisitInstanceDocuments(props.visitInstanceId));
    } catch (e) {
      console.error(e);
      setState({ type: "Error" });
    }
  }, [props.api, props.visitInstanceId, setDocumentsState]);

  React.useEffect(() => {
    initDocumentsState();

    return () => {
      abortToken.current = true;
    };
  }, [initDocumentsState]);

  return {
    documentsState: state,
    isDeletingDocument: isDeletingDocument,
    isUploadingDocument: isUploadingDocument,
    promptDeleteFreeScan: promptDeleteFreeScan,
    uploadNewDocument: uploadNewDocument,
    _internals: {
      initDocumentsState: initDocumentsState,
    },
  };
};

export default useVisitInstanceDocuments;
